/**
 * AnimatedCheckmark — SVG checkmark with stroke-draw animation.
 *
 * Replaces a Lottie success animation with a fully-branded, dependency-free
 * Reanimated + react-native-svg implementation. Bundle impact: ~0KB (SVG +
 * Reanimated already in tree). Renders identically on iOS, Android, and Web.
 *
 * The animation plays in three phases:
 *   1. Circle scale-in (spring, 0 → 1)
 *   2. Checkmark stroke-draw (timing, strokeDashoffset full → 0)
 *   3. Tiny celebratory spring (1 → 1.06 → 1) on the whole group
 *
 * Respects useReducedMotion(): static check rendered immediately, no motion.
 *
 * @example
 * <AnimatedCheckmark size={120} color={COLORS.coral.DEFAULT} onComplete={...} />
 */

import { useEffect } from 'react'
import Animated, {
  useSharedValue,
  useAnimatedProps,
  useAnimatedStyle,
  withSpring,
  withTiming,
  withSequence,
  withDelay,
  useReducedMotion,
  runOnJS,
  Easing,
} from 'react-native-reanimated'
import Svg, { Circle, Path } from 'react-native-svg'

const AnimatedCircle = Animated.createAnimatedComponent(Circle)
const AnimatedPath = Animated.createAnimatedComponent(Path)

interface AnimatedCheckmarkProps {
  /** Outer diameter in px. Default 120. */
  size?: number
  /** Stroke + fill color. Default coral. */
  color?: string
  /** Background fill — when set, the circle is filled instead of stroked. */
  filled?: boolean
  /** Fires after the celebratory spring finishes (~750ms total). */
  onComplete?: () => void
  /** Skip animation entirely — render static check (used internally for SSR + reduced motion). */
  staticOnly?: boolean
}

const DRAW_DURATION = 320
const CIRCLE_SPRING = { damping: 12, stiffness: 180, mass: 0.6 }
const CELEBRATE_SPRING = { damping: 8, stiffness: 200, mass: 0.4 }

// SVG path: 100×100 viewBox, M30,52 L45,67 L72,38 — the canonical "✓".
const CHECK_PATH = 'M30 52 L45 67 L72 38'
// Stroke length for `M30 52 L45 67 L72 38` ≈ 60.6 (Pythag: √(225+225) + √(729+841) ≈ 21.2 + 39.6).
const CHECK_PATH_LENGTH = 61

export function AnimatedCheckmark({
  size = 120,
  color = '#F0422D',
  filled = false,
  onComplete,
  staticOnly = false,
}: AnimatedCheckmarkProps) {
  const reducedMotion = useReducedMotion()
  const shouldAnimate = !staticOnly && !reducedMotion

  const circleRadius = useSharedValue(shouldAnimate ? 0 : 45)
  const checkOffset = useSharedValue(shouldAnimate ? CHECK_PATH_LENGTH : 0)
  const groupScale = useSharedValue(1)

  useEffect(() => {
    if (!shouldAnimate) {
      // Reduced motion or static: jump straight to final state, fire callback.
      circleRadius.value = 45
      checkOffset.value = 0
      if (onComplete) {
        const timer = setTimeout(onComplete, 0)
        return () => clearTimeout(timer)
      }
      return
    }

    // Animation sequence
    circleRadius.value = withSpring(45, CIRCLE_SPRING)
    checkOffset.value = withDelay(
      120,
      withTiming(0, { duration: DRAW_DURATION, easing: Easing.out(Easing.cubic) }),
    )
    groupScale.value = withDelay(
      120 + DRAW_DURATION,
      withSequence(
        withSpring(1.06, CELEBRATE_SPRING),
        withSpring(1, CELEBRATE_SPRING, (finished) => {
          if (finished && onComplete) {
            runOnJS(onComplete)()
          }
        }),
      ),
    )
    return undefined
    // Intentionally void: deps are stable shared values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAnimate])

  const groupStyle = useAnimatedStyle(() => ({
    transform: [{ scale: groupScale.value }],
  }))

  const circleAnimatedProps = useAnimatedProps(() => ({
    r: circleRadius.value,
  }))

  const pathAnimatedProps = useAnimatedProps(() => ({
    strokeDashoffset: checkOffset.value,
  }))

  const strokeWidth = filled ? 0 : Math.max(4, size / 22)
  const checkStroke = filled ? '#FFFFFF' : color
  const checkStrokeWidth = Math.max(5, size / 18)

  return (
    <Animated.View style={[{ width: size, height: size }, groupStyle]}>
      <Svg width={size} height={size} viewBox="0 0 100 100">
        <AnimatedCircle
          cx={50}
          cy={50}
          r={45}
          fill={filled ? color : 'transparent'}
          stroke={filled ? 'transparent' : color}
          strokeWidth={strokeWidth}
          animatedProps={circleAnimatedProps}
        />
        <AnimatedPath
          d={CHECK_PATH}
          stroke={checkStroke}
          strokeWidth={checkStrokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="transparent"
          strokeDasharray={CHECK_PATH_LENGTH}
          animatedProps={pathAnimatedProps}
        />
      </Svg>
    </Animated.View>
  )
}
